import { authStorage, authSessionVersion, invalidateAuthSession } from './authStorage';
import { safeReturnPath } from '../utils/returnPath';

export interface SessionState {
  idleTimeoutMinutes: number;
  idleExpiresAt: string;
}

const TAB_OPEN = 'etappio-session-tab-open';
const RETURN_PATH = 'etappio-idle-return-path';
const DEADLINE = 'sessionIdleExpiresAt';
export const SESSION_ENDED = 'etappio-session-ended';

export function updateSessionState(state?: SessionState) {
  if (state) authStorage.setItem(DEADLINE, state.idleExpiresAt);
}

export function sessionIsIdle() {
  const deadline = Date.parse(authStorage.getItem(DEADLINE) ?? '');
  return Number.isFinite(deadline) && Date.now() >= deadline;
}

export function savedIdleReturnPath() {
  return safeReturnPath(sessionStorage.getItem(RETURN_PATH));
}

export function clearIdleReturnPath() {
  sessionStorage.removeItem(RETURN_PATH);
}

/** Session storage keeps the return view in this open tab only. */
export function clearAuthSession(automatic = false) {
  if (automatic && sessionStorage.getItem(TAB_OPEN)) {
    const path = safeReturnPath(window.location.pathname + window.location.search + window.location.hash);
    if (path && path !== '/') sessionStorage.setItem(RETURN_PATH, path);
  } else {
    clearIdleReturnPath();
  }
  authStorage.setItem('sessionEndReason', automatic ? 'idle' : 'manual');
  invalidateAuthSession();
  for (const key of ['accessToken', 'refreshToken', 'token', 'impersonationToken', DEADLINE]) authStorage.removeItem(key);
  sessionStorage.removeItem(TAB_OPEN);
  window.dispatchEvent(new Event(SESSION_ENDED));
}

export function expireSession() {
  clearAuthSession(true);
  if (window.location.pathname !== '/login') window.location.assign('/login?expired=1');
}

/** Polling validates the session but only real input sends an activity heartbeat. */
export function monitorSession(api: { session: () => Promise<SessionState>; activity: () => Promise<SessionState> }) {
  sessionStorage.setItem(TAB_OPEN, 'true');
  let stopped = false;
  let pendingActivity = false;
  let lastSentAt = 0;
  let requestRunning = false;
  let lastPolicyCheck = Date.now();
  const version = authSessionVersion();

  const check = () => {
    if (stopped || version !== authSessionVersion()) return false;
    if (sessionIsIdle()) { expireSession(); return false; }
    return !!authStorage.getItem('accessToken');
  };
  const sendActivity = async () => {
    if (requestRunning || !pendingActivity || !check()) return;
    pendingActivity = false;
    requestRunning = true;
    lastSentAt = Date.now();
    try {
      const state = await api.activity();
      if (!stopped && version === authSessionVersion() && authStorage.getItem('accessToken')) updateSessionState(state);
    } catch { /* The API interceptor handles an expired session; retry after transient failures. */
      pendingActivity = true;
    } finally { requestRunning = false; }
  };
  const syncPolicy = async () => {
    if (requestRunning) return;
    requestRunning = true;
    try {
      const state = await api.session();
      if (!stopped && version === authSessionVersion() && authStorage.getItem('accessToken')) { updateSessionState(state); check(); }
    } catch { /* Keep the last known deadline when the network is unavailable. */ }
    finally { requestRunning = false; }
  };
  const input = (event: Event) => {
    // Check first: returning from sleep cannot revive an expired session.
    if (!check() || !event.isTrusted || document.visibilityState === 'hidden') return;
    pendingActivity = true;
    if (Date.now() - lastSentAt >= 10000) void sendActivity();
  };
  const wake = () => { if (check() && document.visibilityState !== 'hidden') void syncPolicy(); };
  const storage = (event: StorageEvent) => {
    // Tab-scoped test actors must not inherit another browser session's logout.
    if (sessionStorage.getItem('etappio-local-test-session') === 'true') return;
    if (event.key === 'accessToken' && !authStorage.getItem('accessToken')) {
      clearAuthSession(authStorage.getItem('sessionEndReason') === 'idle');
      if (window.location.pathname !== '/login') window.location.assign('/login');
    } else if (event.key === DEADLINE) check();
  };
  const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
  for (const event of events) window.addEventListener(event, input, { passive: true });
  window.addEventListener('focus', wake);
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('storage', storage);
  const timer = window.setInterval(() => {
    if (!check()) return;
    if (Date.now() - lastSentAt >= 10000) void sendActivity();
    if (Date.now() - lastPolicyCheck >= 60000) {
      lastPolicyCheck = Date.now();
      void syncPolicy();
    }
  }, 1000);
  if (check()) void syncPolicy();
  return () => {
    stopped = true;
    window.clearInterval(timer);
    for (const event of events) window.removeEventListener(event, input);
    window.removeEventListener('focus', wake);
    document.removeEventListener('visibilitychange', wake);
    window.removeEventListener('storage', storage);
  };
}
